import React, { useState } from 'react';
import {
  Box, Container, Grid, Card, CardContent, Typography, Accordion,
  AccordionSummary, AccordionDetails, Chip, Tab, Tabs, Paper, Button,
} from '@mui/material';
import {
  ExpandMore, CheckCircle, AccessTime, Assignment, Payment,
  VerifiedUser, QuestionAnswer, MedicalServices, ArrowForward,
} from '@mui/icons-material';
import PageHero from '../components/common/PageHero';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { newsItems } from '../data/hospitalData';

const PatientsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const tabs = [
    { label: t.patients.visitingHours, icon: <AccessTime /> },
    { label: t.patients.admission, icon: <Assignment /> },
    { label: t.patients.consultation, icon: <MedicalServices /> },
    { label: t.patients.payment, icon: <Payment /> },
    { label: t.patients.rights, icon: <VerifiedUser /> },
    { label: t.patients.faq, icon: <QuestionAnswer /> },
  ];

  const patientSubCards = [
    { icon: '⏰', titleEn: 'Visiting Hours', titleFr: 'Heures de Visite', bgColor: '#0B4F8A' },
    { icon: '📋', titleEn: 'Admission Procedures', titleFr: 'Procédures d\'Admission', bgColor: '#1565C0' },
    { icon: '💳', titleEn: 'Payment & Insurance', titleFr: 'Paiement & Assurance', bgColor: '#0277BD' },
    { icon: '⚖️', titleEn: 'Patient Rights', titleFr: 'Droits du Patient', bgColor: '#01579B' },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default' }}>

      {/* ── Hero ── */}
      <PageHero
        title={language === 'en' ? 'Patient Information & Services' : 'Informations & Services Patients'}
        subtitle={language === 'en'
          ? 'Everything you need to know for a smooth and dignified hospital experience'
          : 'Tout ce que vous devez savoir pour une expérience hospitalière fluide et digne'}
        breadcrumbs={[
          { label: language === 'en' ? 'Home' : 'Accueil', path: '/' },
          { label: language === 'en' ? 'Patients' : 'Patients' },
        ]}
        accentColor="#0B4F8A"
      />

      {/* ── Sub-category cards ── */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={2}>
          {patientSubCards.map((card, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.2s' }}
                onClick={() => setTab(i)}>
                <Box sx={{ height: 140, background: `linear-gradient(135deg, ${card.bgColor}CC 0%, ${card.bgColor}88 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>
                  {card.icon}
                </Box>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.5, mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
                      {language === 'en' ? card.titleEn : card.titleFr}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: 'primary.main', fontSize: '0.82rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                    {language === 'en' ? 'Learn more' : 'En savoir plus'} <ArrowForward sx={{ fontSize: 14 }} />
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* TABS CONTENT */}
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4, overflowX: 'auto' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            {tabs.map((item, i) => (
              <Tab key={i} label={item.label} icon={item.icon} iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 52, fontSize: '0.85rem' }} />
            ))}
          </Tabs>
        </Box>

        {/* Tab 0 – Visiting Hours */}
        {tab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight={700} mb={2}>{t.patients.visitingHours}</Typography>
                  <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={3}>{t.patients.visitingText}</Typography>
                  {[
                    { label: language === 'en' ? 'General Ward' : 'Salle Générale', hours: '10:00 – 12:00 & 16:00 – 18:00' },
                    { label: language === 'en' ? 'ICU / Intensive Care' : 'Soins Intensifs', hours: '11:00 – 12:00 (family only)' },
                    { label: language === 'en' ? 'Maternity' : 'Maternité', hours: '09:00 – 11:00 & 15:00 – 17:00' },
                    { label: language === 'en' ? 'Pediatric Ward' : 'Pédiatrie', hours: '10:00 – 12:00 & 16:00 – 18:00' },
                  ].map((ward, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography fontWeight={600}>{ward.label}</Typography>
                      <Chip label={ward.hours} size="small" color="primary" variant="outlined" />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ bgcolor: 'primary.main', color: 'white', height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight={700} mb={2}>
                    {language === 'en' ? 'Important Notes for Visitors' : 'Notes Importantes pour les Visiteurs'}
                  </Typography>
                  {[
                    language === 'en' ? 'Maximum 2 visitors per patient at a time' : 'Maximum 2 visiteurs par patient à la fois',
                    language === 'en' ? 'Children under 12 not allowed in ICU' : 'Enfants de moins de 12 ans interdits en USI',
                    language === 'en' ? 'Please maintain quiet for patient rest' : 'Maintenir le calme pour le repos des patients',
                    language === 'en' ? 'Mobile phones on silent mode only' : 'Téléphones en mode silencieux uniquement',
                    language === 'en' ? 'Outside food requires nurse approval' : 'Nourriture extérieure : approbation infirmière requise',
                    language === 'en' ? 'Hand sanitizer required on entry and exit' : 'Gel hydroalcoolique obligatoire à l\'entrée et à la sortie',
                  ].map((note, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                      <CheckCircle sx={{ fontSize: 18, color: 'rgba(255,255,255,0.8)', mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>{note}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tab 1 – Admission */}
        {tab === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight={700} mb={3}>{t.patients.admission}</Typography>
                  {(t.patients.admissionSteps as unknown as string[]).map((step, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                        {i + 1}
                      </Box>
                      <Typography variant="body1" lineHeight={1.7} pt={0.5}>{step}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} mb={2}>{language === 'en' ? 'Documents Required' : 'Documents Requis'}</Typography>
                  {[
                    language === 'en' ? 'National ID or Passport' : 'CNI ou Passeport',
                    language === 'en' ? 'Health Insurance Card (if applicable)' : 'Carte d\'Assurance Maladie (si applicable)',
                    language === 'en' ? 'Previous Medical Records (if any)' : 'Dossiers Médicaux Antérieurs (si disponibles)',
                    language === 'en' ? 'Referral Letter (if applicable)' : 'Lettre de Référence (si applicable)',
                    language === 'en' ? 'Emergency contact information' : 'Contact d\'urgence',
                  ].map((doc, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.25 }}>
                      <CheckCircle sx={{ color: 'success.main', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body2" color="text.secondary">{doc}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tab 2 – Consultation */}
        {tab === 2 && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight={700} mb={3}>{t.patients.consultation}</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight={600} mb={2}>{language === 'en' ? 'Consultation Steps' : 'Étapes de Consultation'}</Typography>
                  {(t.patients.consultationSteps as unknown as string[]).map((step, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'secondary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 }}>
                        {i + 1}
                      </Box>
                      <Typography variant="body2" color="text.secondary" lineHeight={1.7} pt={0.5}>{step}</Typography>
                    </Box>
                  ))}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight={600} mb={2}>{language === 'en' ? 'Outpatient Hours' : 'Heures de Consultation Externe'}</Typography>
                  {[
                    { day: language === 'en' ? 'Monday – Friday' : 'Lundi – Vendredi', hours: '07:00 – 18:00' },
                    { day: language === 'en' ? 'Saturday' : 'Samedi', hours: '08:00 – 14:00' },
                    { day: language === 'en' ? 'Sunday & Holidays' : 'Dimanche & Jours Fériés', hours: language === 'en' ? 'Emergency Only' : 'Urgences Uniquement' },
                  ].map((row, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" fontWeight={600}>{row.day}</Typography>
                      <Typography variant="body2" color="text.secondary">{row.hours}</Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Tab 3 – Payment */}
        {tab === 3 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight={700} mb={2}>{t.patients.payment}</Typography>
                  <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={3}>{t.patients.paymentText}</Typography>
                  <Grid container spacing={2}>
                    {[
                      { icon: '💵', en: 'Cash (CFA Francs)', fr: 'Espèces (Francs CFA)' },
                      { icon: '📱', en: 'MTN Mobile Money', fr: 'MTN Mobile Money' },
                      { icon: '🟠', en: 'Orange Money', fr: 'Orange Money' },
                      { icon: '🏛️', en: 'CNPS Insurance', fr: 'Assurance CNPS' },
                    ].map((pm, i) => (
                      <Grid item xs={6} sm={3} key={i}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
                          <Typography fontSize="2rem" mb={0.75}>{pm.icon}</Typography>
                          <Typography variant="body2" fontWeight={600}>{language === 'en' ? pm.en : pm.fr}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ bgcolor: 'warning.main', color: 'white', height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} mb={2}>{language === 'en' ? 'Payment Notice' : 'Avis de Paiement'}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
                    {language === 'en'
                      ? 'Payment is required before non-emergency procedures. We offer flexible payment plans for eligible patients. Please speak to our administrative staff for assistance.'
                      : 'Le paiement est requis avant les procédures non urgentes. Nous offrons des plans de paiement flexibles pour les patients éligibles. Parlez à notre personnel administratif.'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tab 4 – Rights */}
        {tab === 4 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} mb={2} color="primary.main">{language === 'en' ? 'Patient Rights' : 'Droits du Patient'}</Typography>
                  {(t.patients.rightsList as unknown as string[]).slice(0, 3).map((right, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                      <CheckCircle sx={{ color: 'success.main', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{right}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} mb={2} color="secondary.main">{language === 'en' ? 'Patient Responsibilities' : 'Responsabilités du Patient'}</Typography>
                  {(t.patients.rightsList as unknown as string[]).slice(3).map((resp, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                      <CheckCircle sx={{ color: 'secondary.main', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{resp}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tab 5 – FAQ */}
        {tab === 5 && (
          <Box>
            <Typography variant="h5" fontWeight={700} mb={3}>{t.patients.faq}</Typography>
            {(t.patients.faqItems as unknown as { q: string; a: string }[]).map((item, i) => (
              <Accordion key={i} elevation={0} sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.8}>{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Container>

      {/* NEWS SECTION */}
      <Box sx={{ bgcolor: '#f5f7fa', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          {/* Title with underline */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h4" fontWeight={700} color="text.primary" mb={1}>
              {language === 'en' ? 'News' : 'Actualités'}
            </Typography>
            <Box sx={{ width: 60, height: 3, bgcolor: 'primary.main', mx: 'auto', borderRadius: 2 }} />
          </Box>

          <Grid container spacing={3}>
            {newsItems.slice(0, 3).map((item) => (
              <Grid item xs={12} sm={4} key={item.id}>
                <Card
                  onClick={() => navigate('/news')}
                  sx={{
                    cursor: 'pointer', height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                  }}
                >
                  {/* Photo */}
                  <Box
                    sx={{
                      height: 200,
                      bgcolor: item.image,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <MedicalServices sx={{ fontSize: 50, color: 'rgba(255,255,255,0.35)' }} />
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ lineHeight: 1.4 }}>
                      {language === 'en' ? item.titleEn : item.titleFr}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                      {(language === 'en' ? item.excerptEn : item.excerptFr).slice(0, 140)}…
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="outlined" size="large" endIcon={<ArrowForward />} onClick={() => navigate('/news')}>
              {language === 'en' ? 'All News' : 'Toutes les Actualités'}
            </Button>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default PatientsPage;
